import {
  APIV1Version,
  ClientType,
  getStoreName,
} from '@buncha/common/types/common';
import {OrderVerifyStatus, OrdersForValidation} from '@buncha/B2B/types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  useGetOrdersForValidationAndUpdationCheckout,
  useOrderValidationUpdate,
  useUpdateB2BOrder,
} from '@buncha/B2B/hooks/B2BChekout';

import {Analytics} from '@buncha/config/analytics/analytics';
import CheckoutItem from '../common/CheckoutItem';
import {EventType} from '@buncha/config/analytics/type';
import {HomeScreens} from '@buncha/home/navigation/types';
import {Modules} from '@buncha/utils/types';
import {smartLook} from '@buncha/config/analytics';
import {useB2BCheckoutContext} from '@buncha/B2B/hooks/B2BCheckoutReducer';
import {useGetLocale} from '@buncha/localization/hooks';

interface VerificationItemsProps {
  runId: number;
  item: OrdersForValidation;
  getLoading?: (loading: boolean) => void;
  loading: boolean;
  storeId?: number;
  addItem: (item: OrdersForValidation) => void;
  toggleCancelModal: () => void;
}

const VerificationItems: React.FC<VerificationItemsProps> = props => {
  const {
    runId,
    item,
    getLoading,
    loading,
    storeId,
    addItem,
    toggleCancelModal,
  } = props;
  const {dispatch} = useB2BCheckoutContext();
  const [itemLoading, setItemLoading] = useState(false);
  const [updateB2BOrder] = useUpdateB2BOrder(APIV1Version.V100);
  const {title, body, button} =
    useGetLocale(Modules.B2B, HomeScreens.B2B) ?? {};
  const [getOrdersForValidationAndUpdation] =
    useGetOrdersForValidationAndUpdationCheckout(APIV1Version.V100, dispatch);
  const [orderValidationUpdate, updateState] = useOrderValidationUpdate(
    APIV1Version.V100,
    dispatch,
  );
  const emptyOrder = !item.order.items.length;
  const buttonText = useMemo(() => {
    if (emptyOrder) return 'Cancel Order';
    if (
      item?.status === OrderVerifyStatus.validationFailed &&
      Boolean(!item?.defectedItems?.length)
    )
      return button?.verifyAgain;

    return button?.verifyOrder;
  }, [
    button?.verifyAgain,
    button?.verifyOrder,
    emptyOrder,
    item?.defectedItems?.length,
    item?.status,
  ]);

  const updateOrderStatus = useCallback(
    async (orderId: number) => {
      setItemLoading(true);
      await updateB2BOrder([orderId]);
      await getOrdersForValidationAndUpdation(runId);
      setItemLoading(false);
    },
    [getOrdersForValidationAndUpdation, runId, updateB2BOrder],
  );
  const onSubmit = useCallback(
    async (orderId: number) => {
      if (emptyOrder) return toggleCancelModal();

      if (item.status === OrderVerifyStatus.validationDone)
        return await updateOrderStatus(orderId);
      orderValidationUpdate([orderId], runId);
      const eventParams = {
        type: EventType.cta,
        runId: runId,
        client: ClientType.meijer,
        legacyId: orderId,
        storeType: getStoreName(String(storeId)),
      };
      Analytics.eventWithProps('verify order', eventParams);
      smartLook.trackCustomEvent('verify order', eventParams);
    },
    [
      emptyOrder,
      item.status,
      orderValidationUpdate,
      runId,
      storeId,
      toggleCancelModal,
      updateOrderStatus,
    ],
  );

  const addItemChange = useCallback(() => {
    addItem(item);
  }, [addItem, item]);

  useEffect(() => {
    getLoading?.(updateState.loading);
  }, [getLoading, updateState.loading]);

  return (
    <CheckoutItem
      status={item?.status}
      order={item?.order}
      defectedItem={item?.defectedItems}
      hideItemButton={
        item?.status !== OrderVerifyStatus.pending &&
        Boolean(item?.defectedItems?.length)
      }
      btnLoading={updateState.loading || loading || itemLoading}
      onButtonClick={onSubmit}
      loadingFooter={{
        title: title?.verificationProgress,
        subText: body?.etaMinutes,
      }}
      failedFooter={{
        title: title?.verificationFailed,
        subText: body?.dropToContinue,
      }}
      buttonTitle={buttonText}
      btnLoadingTitle={button?.verifyingOrder}
      addItemChange={addItemChange}
    />
  );
};

export default React.memo(VerificationItems);
